import { useEffect, useState } from "react";
import { noop } from "lodash";
import { useQueryClient } from "react-query";
import { getEventRegistrationUrl } from "@/react/helpers/loginHelpers";
import type { CurrentUser } from "@/react/types/CurrentUser";
import { useCommunitySignup } from "@circle-react/components/Events/hooks/useCommunitySignup";
import { useIsEventOwner } from "@circle-react/components/Events/hooks/useIsEventOwner";
import { useCurrentUser } from "@circle-react/contexts";
import { useRecurringEventRsvpApi } from "@circle-react/hooks/events/useRecurringEventRsvpApi";
import type { Event } from "@circle-react/types/Event";

interface RecurringRsvpModalProps {
  event: Event;
  eventRsvpedByDefault?: boolean;
  onChange?: (event: Event) => void;
  onClose?: () => void;
}

interface HandleUserRedirectProps {
  canUsersSignUp: boolean;
  currentUser?: CurrentUser | null;
  userSignUpUrl: string | null;
}

const getSelectedEventIds = (eventsState: Record<string, boolean>) =>
  Object.entries(eventsState)
    .filter(([, value]) => value)
    .map(([key]) => key);

const isCurrentEventConfirmed = (eventIds: string[], eventId: number) =>
  eventIds.find(e => Number(e) === eventId);

const handleUserRedirect = ({
  currentUser,
  canUsersSignUp,
  userSignUpUrl,
}: HandleUserRedirectProps) => {
  if (!currentUser) {
    window.location.href = getEventRegistrationUrl(
      canUsersSignUp,
      userSignUpUrl ?? "",
      true,
    );
    return true;
  }
  return false;
};

export const useRecurringRsvpModal = ({
  event,
  onClose = noop,
  onChange = noop,
  eventRsvpedByDefault = true,
}: RecurringRsvpModalProps) => {
  const [isRecurringRsvpOpen, setIsRecurringRsvpOpen] = useState(false);
  const [eventsState, setEventsState] = useState<Record<string, boolean>>({});
  const [eventsSelectedCount, setEventsSelectedCount] = useState(0);
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const isEventOwner = useIsEventOwner(event);
  const role = isEventOwner ? "host" : "participant";

  const { isLoading, events, fetchEvents, onRecurringRsvpSubmit } =
    useRecurringEventRsvpApi({
      event,
    });

  const { canUsersSignUp, userSignUpUrl } = useCommunitySignup();

  const openRecurringRsvpModal = () => {
    const wasRedirected = handleUserRedirect({
      currentUser,
      canUsersSignUp,
      userSignUpUrl,
    });
    if (wasRedirected) return;

    setIsRecurringRsvpOpen(isRecurringRsvpOpen => !isRecurringRsvpOpen);
  };

  const onRecurringModalClose = () => {
    setIsRecurringRsvpOpen(false);
  };

  const onSelectAllClickHandler = () => {
    setEventsState(prevEventState => {
      const newState: Record<string, boolean> = {};
      const eventsEntries = Object.entries(prevEventState);
      const areAllSelected = eventsEntries.every(item => item[1]);

      for (const [key] of eventsEntries) {
        newState[key] = !areAllSelected;
      }
      return newState;
    });
  };

  const onCheckboxChange = ({
    name,
    value,
  }: {
    name: string;
    value: boolean;
  }) => {
    setEventsState(prevEventsState => ({
      ...prevEventsState,
      [name]: value,
    }));
  };

  const onSubmit = async () => {
    const eventIds = getSelectedEventIds(eventsState);
    const eventConfirmed = isCurrentEventConfirmed(eventIds, event.id);

    await onRecurringRsvpSubmit({ eventIds, role });
    onChange({ ...event, rsvped_event: Boolean(eventConfirmed) });
    reloadEventList();
    onClose();
  };

  const reloadEventList = () => {
    void queryClient.invalidateQueries("posts");
  };

  useEffect(() => {
    if (!events) {
      return;
    }
    const values: Record<string, boolean> = {};
    events.forEach(item => {
      values[`${item.id}`] =
        eventRsvpedByDefault && item.id === event.id ? true : item.rsvped_event;
    });
    setEventsState(values);
  }, [events]);

  useEffect(() => {
    if (!eventsState) {
      return;
    }

    setEventsSelectedCount(
      Object.values(eventsState).filter(item => item).length,
    );
  }, [eventsState]);

  const availableEvents = events.filter(item => !item.rsvp_limit_reached);

  return {
    isLoading,
    events: availableEvents,
    eventsState,
    isRecurringRsvpOpen,
    eventsSelectedCount,
    fetchEvents,
    onSubmit,
    onCheckboxChange,
    onSelectAllClickHandler,
    openRecurringRsvpModal,
    onRecurringModalClose,
  };
};
